import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { BrandText } from '../components/BrandText';
import { useCursor } from '../context/CursorContext';
import { AnimatedSection, StaggerChildren } from '../components/AnimatedSection';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { TiltCard } from '../components/TiltCard';
import { FloatingParticles } from '../components/FloatingParticles';
import { brandBenefits, creatorBenefits, featuredCreators as fallbackFeatured, stats as fallbackStats } from '../data/mock';
import { useYouTubeCreators, formatCount } from '../hooks/useYouTube';

export default function Home() {
  const { setLabel } = useCursor();
  const { data: ytData } = useYouTubeCreators();

  const agg = ytData?.aggregated;
  const liveStats = agg
    ? [
        { value: formatCount(agg.total_subscribers), label: 'Combined Subscribers' },
        { value: formatCount(agg.total_views), label: 'Total Views' },
        { value: String(agg.total_creators), label: 'Creators' },
        { value: formatCount(agg.total_videos), label: 'Videos Published' },
      ]
    : fallbackStats;

  const liveFeatured = ytData?.creators?.filter((c) => c.featured);
  const featuredCreators = liveFeatured && liveFeatured.length > 0 ? liveFeatured : fallbackFeatured;

  return (
    <div>
      {/* Hero */}
      <section className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-green-950/20 via-black to-black" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-green-500/5 rounded-full blur-3xl animate-glow-pulse" />
        <FloatingParticles count={25} />

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <div
            className="flex justify-center mb-8 animate-hero-reveal animate-float"
            onMouseEnter={() => setLabel('The Moon Media')}
            onMouseLeave={() => setLabel('')}
          >
            <Logo size="hero" />
          </div>

          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] text-xs mb-4 animate-text-reveal" style={{ animationDelay: '0.3s' }}>
            Web3 Creator Marketing Agency
          </p>

          <h1 className="text-6xl md:text-8xl mb-6 animate-text-reveal" style={{ animationDelay: '0.5s' }}>
            <BrandText />
          </h1>

          <p className="text-white/50 text-lg max-w-xl mx-auto mb-10 animate-text-reveal" style={{ animationDelay: '0.7s' }}>
            Reach the right audience through trusted crypto creators.
          </p>

          <div className="flex gap-4 justify-center flex-wrap animate-text-reveal" style={{ animationDelay: '0.9s' }}>
            <Link
              to="/get-started"
              className="group relative px-8 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden"
              data-testid="hero-brand-cta"
              onMouseEnter={() => setLabel('Start')}
              onMouseLeave={() => setLabel('')}
            >
              <span className="relative z-10">Market Your Brand</span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
            <Link
              to="/get-started?tab=creator"
              className="group px-8 py-4 border border-green-500/40 text-green-500 font-bold uppercase tracking-wider text-sm hover:bg-green-500/10 transition-colors duration-300 relative overflow-hidden"
              data-testid="hero-creator-cta"
              onMouseEnter={() => setLabel('Join')}
              onMouseLeave={() => setLabel('')}
            >
              <span className="relative z-10">Join as Creator</span>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <p className="text-white/30 text-[10px] uppercase tracking-widest">Scroll to explore</p>
          <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-green-500 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Animated divider */}
      <div className="relative py-4 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="text-white/[0.03] text-7xl font-black uppercase mx-8 select-none">
              THE MOON MEDIA
            </span>
          ))}
        </div>
      </div>

      {/* Stats */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <p className="text-white/40 text-sm uppercase tracking-wider">
              Your Brand's Reach Through Our Network
            </p>
          </AnimatedSection>
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-4 gap-4" staggerDelay={150}>
            {liveStats.map((stat, i) => (
              <TiltCard
                key={i}
                cursorLabel="Stat"
                className="text-center p-8 border border-white/5 bg-white/[0.02] hover:border-green-500/30"
              >
                <AnimatedCounter
                  value={stat.value}
                  className="text-3xl md:text-4xl font-black text-white mb-1 block"
                />
                <p className="text-white/40 uppercase tracking-wider text-[10px]">{stat.label}</p>
              </TiltCard>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* Featured Creators */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Featured</p>
            <h2 className="text-4xl md:text-5xl font-black text-white">TOP CREATORS</h2>
            <p className="text-white/30 text-sm mt-3">The voices your audience already trusts.</p>
          </AnimatedSection>

          <StaggerChildren className="grid md:grid-cols-3 gap-4" staggerDelay={200}>
            {featuredCreators.map((creator) => (
              <TiltCard
                key={creator.handle}
                cursorLabel="View"
                glowColor="rgba(34, 197, 94, 0.2)"
                className="p-6 border border-green-500/30 bg-green-500/5 hover:border-green-500/50 relative"
              >
                <span className="absolute top-4 right-4 px-2 py-0.5 bg-green-500 text-black text-[9px] font-bold uppercase">Featured</span>
                {creator.thumbnail ? (
                  <img src={creator.thumbnail} alt={creator.display_name} className="w-12 h-12 rounded-full mb-4 border border-green-500/30" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 animate-pulse-glow">
                    <span className="text-lg font-black text-black">{creator.display_name[0]}</span>
                  </div>
                )}
                <h3 className="text-xl font-black text-white mb-1">{creator.display_name}</h3>
                <p className="text-green-500 text-sm font-semibold mb-2">{formatCount(creator.subscriber_count)} subscribers</p>
                <p className="text-white/50 text-sm">{creator.description}</p>
              </TiltCard>
            ))}
          </StaggerChildren>

          <AnimatedSection className="text-center mt-10" delay={400}>
            <Link
              to="/creators"
              className="group inline-flex items-center gap-2 text-green-500 text-sm font-semibold uppercase tracking-wider hover:text-green-400 transition-colors duration-300"
              onMouseEnter={() => setLabel('All')}
              onMouseLeave={() => setLabel('')}
            >
              View All Creators
              <span className="group-hover:translate-x-1 transition-transform duration-300">&rarr;</span>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Brands Choose Us */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-950/5 to-transparent" />
        <div className="max-w-5xl mx-auto relative z-10">
          <AnimatedSection className="text-center mb-16">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">For Brands</p>
            <h2 className="text-4xl md:text-5xl font-black text-white">WHY BRANDS CHOOSE US</h2>
          </AnimatedSection>
          <StaggerChildren className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={120}>
            {brandBenefits.map((b, i) => (
              <TiltCard key={i} cursorLabel="Learn" className="p-6 border border-green-500/20 bg-green-500/5 hover:border-green-500/40">
                <p className="text-green-500 text-2xl font-black mb-4 opacity-50">{b.number}</p>
                <h3 className="text-lg font-bold text-white mb-2">{b.title}</h3>
                <p className="text-white/40 text-sm">{b.description}</p>
                <div className="mt-4 h-px bg-gradient-to-r from-green-500/0 via-green-500/40 to-green-500/0 shimmer-border" />
              </TiltCard>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* Why Creators Join Us */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">For Creators</p>
            <h2 className="text-4xl md:text-5xl font-black text-white">WHY CREATORS JOIN US</h2>
          </AnimatedSection>
          <StaggerChildren className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={120}>
            {creatorBenefits.map((b, i) => (
              <TiltCard key={i} cursorLabel="Learn" className="p-6 border border-white/5 bg-white/[0.02] hover:border-green-500/30">
                <p className="text-green-500 text-2xl font-black mb-4 opacity-50">{b.number}</p>
                <h3 className="text-lg font-bold text-white mb-2">{b.title}</h3>
                <p className="text-white/40 text-sm">{b.description}</p>
                <div className="mt-4 h-px bg-gradient-to-r from-green-500/0 via-green-500/40 to-green-500/0 shimmer-border" />
              </TiltCard>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <AnimatedSection className="max-w-3xl mx-auto">
          <div className="p-12 border border-green-500/20 bg-gradient-to-b from-green-500/10 to-transparent text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 animate-gradient" />
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <div className="animate-float"><Logo size="lg" /></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
                Ready to Reach Your Audience?
              </h2>
              <p className="text-white/50 mb-8 max-w-md mx-auto">
                Market your brand through the top crypto creators in Web3.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link
                  to="/get-started"
                  className="group relative inline-block px-10 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden"
                  onMouseEnter={() => setLabel('Start')}
                  onMouseLeave={() => setLabel('')}
                >
                  <span className="relative z-10">Get Started</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
                <Link
                  to="/get-started?tab=creator"
                  className="group px-10 py-4 border border-green-500/40 text-green-500 font-bold uppercase tracking-wider text-sm hover:bg-green-500/10 transition-colors duration-300"
                  onMouseEnter={() => setLabel('Join')}
                  onMouseLeave={() => setLabel('')}
                >
                  <span className="relative z-10">Join as Creator</span>
                </Link>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}
